import { ChevronDownIcon } from '@chakra-ui/icons';
import {
    Button,
    HStack,
    Input,
    Popover,
    PopoverArrow,
    PopoverBody,
    PopoverCloseButton,
    PopoverContent,
    PopoverHeader,
    PopoverTrigger,
    Radio,
    RadioGroup,
    VStack,
} from '@chakra-ui/react';
import { useState } from 'react';
import { translate } from '../../../i18n';
import { DateRangePickerIntervalType } from '../AnalyticsPage';

type Props = {
    intervalType: DateRangePickerIntervalType;
    setIntervalType: (type: DateRangePickerIntervalType) => void;
    isDemo?: boolean;
    customStartDate?: string;
    customEndDate?: string;
    onCustomStartDateChange?: (date: string) => void;
    onCustomEndDateChange?: (date: string) => void;
};

const items: { value: DateRangePickerIntervalType; label: string }[] = [
    { value: 'All Time', label: translate('analytics.selector.all_time') },
    { value: 'Last 24 Hours', label: translate('analytics.selector.last_24_hours') },
    { value: 'Last 7 Days', label: translate('analytics.selector.last_7_days') },
    { value: 'Last 30 Days', label: translate('analytics.selector.last_30_days') },
    { value: 'Last 90 Days', label: translate('analytics.selector.last_90_days') },
    { value: 'Custom', label: translate('analytics.selector.custom') },
];

const DEMO_HIDDEN: DateRangePickerIntervalType[] = ['Last 24 Hours', 'Last 7 Days'];

export default function DateRangePicker({
    intervalType,
    setIntervalType,
    isDemo,
    customStartDate,
    customEndDate,
    onCustomStartDateChange,
    onCustomEndDateChange,
}: Props) {
    const [isOpen, setIsOpen] = useState(false);
    const visibleItems = isDemo ? items.filter(i => !DEMO_HIDDEN.includes(i.value)) : items;

    const label =
        intervalType === 'Custom' && customStartDate && customEndDate
            ? `${customStartDate} – ${customEndDate}`
            : visibleItems.find(i => i.value === intervalType)?.label ?? intervalType;

    return (
        <Popover isOpen={isOpen} onClose={() => setIsOpen(false)}>
            <PopoverTrigger>
                <Button
                    onClick={() => setIsOpen(true)}
                    variant='outline'
                    size='sm'
                    rightIcon={<ChevronDownIcon />}
                    fontWeight='600'
                    fontSize='sm'
                >
                    {label}
                </Button>
            </PopoverTrigger>
            <PopoverContent>
                <PopoverArrow />
                <PopoverCloseButton />
                <PopoverHeader fontWeight='600'>{translate('analytics.selector.time_range')}</PopoverHeader>
                <PopoverBody>
                    <VStack align='stretch' spacing='3'>
                        <RadioGroup
                            value={intervalType}
                            onChange={value => setIntervalType(value as DateRangePickerIntervalType)}
                        >
                            <VStack align='start' spacing='2'>
                                {visibleItems.map(item => (
                                    <Radio key={item.value} value={item.value}>
                                        {item.label}
                                    </Radio>
                                ))}
                            </VStack>
                        </RadioGroup>
                        {intervalType === 'Custom' && (
                            <HStack spacing='2' pt='2'>
                                <Input
                                    type='date'
                                    size='sm'
                                    value={customStartDate ?? ''}
                                    onChange={e => onCustomStartDateChange?.(e.target.value)}
                                    max={customEndDate}
                                />
                                <Input
                                    type='date'
                                    size='sm'
                                    value={customEndDate ?? ''}
                                    onChange={e => onCustomEndDateChange?.(e.target.value)}
                                    min={customStartDate}
                                />
                            </HStack>
                        )}
                    </VStack>
                </PopoverBody>
            </PopoverContent>
        </Popover>
    );
}
